import React from "react";
import { motion } from "framer-motion";
import NewsletterSection from "../components/NewsletterSection";
import SEO from "../components/SEO";
import ShareButtons from "../components/ShareButtons";

export default function About() {
  return (
    <main data-testid="about-page" className="bg-[#FDFBF7]">
      <SEO
        title="About — Ashwini & Hemant Parulkar"
        description="We retired in 2015 and packed two suitcases. Globezing is our slow-travel journal. Think global, live local."
        path="/about"
      />
      <section className="pt-36 pb-10 lg:pt-44">
        <div className="max-w-6xl mx-auto px-6 lg:px-10 grid grid-cols-1 md:grid-cols-12 gap-10 lg:gap-16 items-end">
          <div className="md:col-span-7">
            <p className="kicker">About</p>
            <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl font-light leading-[0.98] tracking-tight mt-5">
              Think global,
              <br /> <em className="italic">live local.</em>
            </h1>
            <p className="mt-8 text-lg text-stone-700 leading-relaxed max-w-xl">
              We are Ashwini &amp; Hemant Parulkar — originally from India, New Zealand citizens, former residents of Singapore and California. We retired in February 2015 in our late fifties and early sixties, packed two suitcases, and set out to find out where home might live next.
            </p>
            <div className="mt-8">
              <ShareButtons title="About Globezing — Ashwini & Hemant Parulkar" description="Why we retired early and packed two suitcases." />
            </div>
          </div>
          <motion.div
            initial={{ opacity: 0, scale: 1.02 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 1.2 }}
            className="md:col-span-5"
          >
            <div className="aspect-[3/4] overflow-hidden bg-stone-200">
              <img
                src="/api/uploads/6f6353aa7ec1412d9d0cdfc9ef5fea2e.jpg"
                alt="Ashwini & Hemant Parulkar"
                className="w-full h-full object-cover"
              />
            </div>
            <p className="kicker text-stone-500 mt-3">Ashwini &amp; Hemant · est. 2015</p>
          </motion.div>
        </div>
      </section>

      {/* Manifesto */}
      <section className="py-20 lg:py-28 border-y border-stone-200 bg-stone-50">
        <div className="max-w-3xl mx-auto px-6 lg:px-0">
          <p className="kicker">Our Manifesto</p>
          <div className="mt-8 space-y-8 text-[17px] sm:text-[18px] leading-[1.8] text-stone-800">
            <p><span className="font-serif text-5xl float-left mr-3 mt-1 leading-[0.8] text-[#9c4122]">T</span>hree years ago, my friends Lynne and Tim Martin said, <em>"Postpone nothing."</em> This is so true. Life is meant for living; life well-loved is a life well-lived, no matter the circumstances. Our decision to retire early was not easy — I have cancer in my family and my husband's family has heart disease. When someone you love becomes terminally ill, the sense of mortality heightens.</p>
            <p>We decided to travel with our suitcases and live like locals. We stay a minimum of one month in each location to get a real feel for the place. We rent furnished one- or two-bedroom apartments. We use local transport. We shop at farmers' markets. We cook what the locals cook.</p>
            <p>Our motto is <strong>"Think Global Live Local."</strong> Home has always been where we are together. The world is our oyster.</p>
          </div>

          <ul className="mt-16 space-y-6 border-t border-stone-300 pt-8">
            {[
              ["01", "Traveling this way takes a certain mindset — and a lot of discipline."],
              ["02", "Stay within the budget. Many attractions beckon on the road."],
              ["03", "Enjoy each other's company. Most of the time there's no one else to turn to."],
              ["04", "Accept different ways of life, languages, cultures, food. Vegetarians — plan accordingly."],
              ["05", "Make friends everywhere. People are mostly the same all over the world."],
              ["06", "Postpone nothing. (Thank you, Lynne and Tim Martin.)"],
            ].map(([n, t]) => (
              <li key={n} className="grid grid-cols-12 gap-4 items-start">
                <span className="col-span-2 font-serif text-3xl italic text-[#9c4122]">{n}</span>
                <span className="col-span-10 text-stone-800 text-[16px] leading-relaxed">{t}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Stats */}
      <section data-testid="about-stats" className="py-20 lg:py-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid grid-cols-2 md:grid-cols-4 gap-10">
          {[
            ["Countries", "24+"],
            ["Cities called home", "38"],
            ["Years on the road", "10"],
            ["Suitcases", "2"],
          ].map(([label, value]) => (
            <div key={label}>
              <p className="font-serif text-5xl sm:text-6xl lg:text-7xl font-light text-stone-900">{value}</p>
              <p className="kicker text-stone-500 mt-3">{label}</p>
            </div>
          ))}
        </div>
      </section>

      <NewsletterSection />
    </main>
  );
}
